/* eslint-disable jsx-a11y/no-static-element-interactions */
import type { ListTypeListItem } from '@/types/list-type';
import clsx from 'clsx';
import styles from './styles.module.scss';
import { useEffect, useRef, useState } from 'react';

interface HotplaceItemListBarProps {
    items: ListTypeListItem[];
    selectedId: string;
    onSelect: (id: string) => void;
    isHorizontal: boolean;
    halfMode?: boolean;
    tabIndexBase?: number;
}

const HotplaceItemListBar = ({ items, selectedId, onSelect, isHorizontal, halfMode = false, tabIndexBase = 5 }: HotplaceItemListBarProps) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [isOverflowing, setIsOverflowing] = useState(false);

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        setIsOverflowing(el.scrollWidth > el.clientWidth);
    }, [items]);

    const isDown = useRef(false);
    const startX = useRef(0);
    const scrollLeft = useRef(0);

    const handleMouseDown = (e: { pageX: number }) => {
        const el = containerRef.current;
        if (!el) return;
        isDown.current = true;
        el.style.cursor = 'grabbing';
        startX.current = e.pageX - el.offsetLeft;
        scrollLeft.current = el.scrollLeft;
    };

    const handleMouseLeave = () => {
        isDown.current = false;
        if (containerRef.current) {
            containerRef.current.style.cursor = 'grab';
        }
    };

    const handleMouseUp = () => {
        isDown.current = false;
        if (containerRef.current) {
            containerRef.current.style.cursor = 'grab';
        }
    };

    const handleMouseMove = (e: { preventDefault: () => void; pageX: number }) => {
        if (!isDown.current) return;
        const el = containerRef.current;
        if (!el) return;
        e.preventDefault();
        const x = e.pageX - el.offsetLeft;
        const walk = (x - startX.current) * 2;
        el.scrollLeft = scrollLeft.current - walk;
    };

    return (
        <div
            ref={containerRef}
            className={clsx(styles.bar, {
                [styles['bar--horizontal']]: isHorizontal,
                [styles['bar--vertical']]: !isHorizontal,
                [styles['bar--halfmode']]: halfMode,
                [styles['bar--overflow']]: isOverflowing,
            })}
            data-region-nav
            data-region-order={tabIndexBase}
            onMouseDown={handleMouseDown}
            onMouseLeave={handleMouseLeave}
            onMouseUp={handleMouseUp}
            onMouseMove={handleMouseMove}
        >
            {items.map((item, index) => {
                const isActive = String(item.id) === String(selectedId);
                return (
                    // eslint-disable-next-line jsx-a11y/role-supports-aria-props
                    <button
                        key={item.id}
                        type="button"
                        role="tab"
                        onClick={() => onSelect(item.id)}
                        className={clsx(styles.pill, 'focus-border', {
                            [styles['pill--active']]: isActive,
                        })}
                        tabIndex={tabIndexBase + index}
                        data-interactive
                        aria-selected={isActive}
                        aria-checked={isActive}
                    >
                        {item.title}
                    </button>
                );
            })}
        </div>
    );
};

export default HotplaceItemListBar;
